import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTeam } from '../contexts/TeamContext';
import { Link } from 'react-router-dom';
import { API_URL } from '../services/api';
import { useApiHeaders } from '../hooks/useApiHeaders';
import Layout from '../components/Layout';
import PlatformInstallGuide, { type TokenError } from '../components/PlatformInstallGuide';
import { aggregateSecurityRisk, type AggregateSecurityRisk, type GatewayAssessment } from '../utils/securityRisk';
import type { PolicyConfig } from '../components/PolicyConfigTabs';

interface Agent {
	agent_id: string;
	user_id: string;
	hostname: string;
	platform: string;
	version: string;
	status: 'online' | 'offline';
	last_heartbeat: number;
	created_at: number;
	latest_version?: string;
	update_available?: boolean;
}

interface Instance {
	instance_id: string;
	agent_id: string;
	instance_name?: string;
	gateway_port?: number;
	clawdbot_version: string;
	install_path: string;
	running_status: 'running' | 'stopped' | 'error';
	bot_type?: string;
	agent_hostname?: string;
}

export default function DashboardPage() {
	const { user, firebaseUser } = useAuth();
	const { currentTeam } = useTeam();
	const { getHeaders } = useApiHeaders();
	const [showInstallInstructions, setShowInstallInstructions] = useState(false);
	const [registrationToken, setRegistrationToken] = useState<string | null>(null);
	const [tokenLoading, setTokenLoading] = useState(true);
	const [tokenError, setTokenError] = useState<TokenError | null>(null);
	const [agents, setAgents] = useState<Agent[]>([]);
	const [instances, setInstances] = useState<Instance[]>([]);
	const [securityInstances, setSecurityInstances] = useState<{ config: PolicyConfig; gateway_assessment?: GatewayAssessment | null; gateway_assessed_at?: number | null }[]>([]);
	const [teamMemberCount, setTeamMemberCount] = useState<number>(0);
	const [showIssues, setShowIssues] = useState(false);

	// Fetch registration token
	useEffect(() => {
		let cancelled = false;

		const fetchToken = async () => {
			if (!firebaseUser) { setTokenLoading(false); return; }

			try {
				const headers = await getHeaders();
				const response = await fetch(`${API_URL}/api/v1/agents/token?server_url=${encodeURIComponent(API_URL)}`, {
					headers,
				});

				if (cancelled) return;
				if (response.ok) {
					const data = await response.json();
					setRegistrationToken(data.token);
					setTokenError(null);
				} else if (response.status === 403) {
					const errorData = await response.json().catch(() => ({ error: 'Forbidden', message: 'Access denied' }));
					setTokenError(errorData);
				}
			} catch (error) {
				if (!cancelled) console.error('Error fetching registration token:', error);
			} finally {
				if (!cancelled) setTokenLoading(false);
			}
		};

		fetchToken();
		return () => { cancelled = true; };
	}, [firebaseUser, currentTeam]);

	// Fetch agents + instances
	useEffect(() => {
		let cancelled = false;

		const fetchData = async () => {
			if (!firebaseUser) return;

			try {
				const headers = await getHeaders();

				const [agentsRes, instancesRes] = await Promise.all([
					fetch(`${API_URL}/api/v1/agents`, { headers }),
					fetch(`${API_URL}/api/v1/instances`, { headers }),
				]);

				if (cancelled) return;

				if (agentsRes.ok) {
					const data = await agentsRes.json();
					setAgents(data.agents || []);
				}
				if (instancesRes.ok) {
					const data = await instancesRes.json();
					setInstances(data.instances || []);
				}
			} catch (error) {
				if (!cancelled) console.error('Error fetching data:', error);
			}
		};

		fetchData();
		const interval = setInterval(fetchData, 30000);
		return () => { cancelled = true; clearInterval(interval); };
	}, [firebaseUser, currentTeam]);

	// Fetch security summary
	useEffect(() => {
		let cancelled = false;

		const fetchSecurity = async () => {
			if (!firebaseUser) return;

			try {
				const headers = await getHeaders();
				const response = await fetch(`${API_URL}/api/v1/policies/security-summary`, {
					headers,
				});

				if (cancelled) return;
				if (response.ok) {
					const data = await response.json();
					setSecurityInstances(data.instances || []);
				}
			} catch (error) {
				if (!cancelled) console.error('Error fetching security summary:', error);
			}
		};

		fetchSecurity();
		const interval = setInterval(fetchSecurity, 30000);
		return () => { cancelled = true; clearInterval(interval); };
	}, [firebaseUser, currentTeam]);

	// Fetch team member count
	useEffect(() => {
		let cancelled = false;

		const fetchTeamMembers = async () => {
			if (!firebaseUser) return;

			try {
				const headers = await getHeaders();
				const response = await fetch(`${API_URL}/api/v1/team/members`, {
					headers,
				});

				if (cancelled) return;
				if (response.ok) {
					const data = await response.json();
					setTeamMemberCount(data.members?.length || 1);
				}
			} catch {
				if (!cancelled) setTeamMemberCount(1);
			}
		};

		fetchTeamMembers();
		return () => { cancelled = true; };
	}, [firebaseUser, currentTeam]);

	const securityRisk: AggregateSecurityRisk = useMemo(
		() => aggregateSecurityRisk(securityInstances),
		[securityInstances]
	);

	const issueCount = securityRisk.instancesWithThreatDetectionOff
		+ securityRisk.instancesWithWeakThreatActions
		+ securityRisk.instancesWithPamOff
		+ securityRisk.instancesWithWeakPam
		+ securityRisk.instancesWithDirectChannels;

	const issueDetails = useMemo(() => {
		const items: { label: string; count: number; severity: 'critical' | 'warning'; link?: string; workspaces: string[] }[] = [];
		if (securityRisk.instancesWithThreatDetectionOff > 0)
			items.push({ label: 'Threat detection disabled', count: securityRisk.instancesWithThreatDetectionOff, severity: 'critical', link: '/settings', workspaces: securityRisk.threatDetectionOffWorkspaces });
		if (securityRisk.instancesWithWeakThreatActions > 0)
			items.push({ label: 'Weak threat actions (no block/OTP on high severity)', count: securityRisk.instancesWithWeakThreatActions, severity: 'warning', link: '/settings', workspaces: securityRisk.weakThreatActionsWorkspaces });
		if (securityRisk.instancesWithPamOff > 0)
			items.push({ label: 'Privileged Access Management disabled', count: securityRisk.instancesWithPamOff, severity: 'critical', link: '/settings', workspaces: securityRisk.pamOffWorkspaces });
		if (securityRisk.instancesWithWeakPam > 0)
			items.push({ label: 'Weak PAM config (destructive/credential ops not protected)', count: securityRisk.instancesWithWeakPam, severity: 'warning', link: '/settings', workspaces: securityRisk.weakPamWorkspaces });
		if (securityRisk.instancesWithDirectChannels > 0)
			items.push({ label: `Unmanaged channels (${securityRisk.directChannelNames.join(', ')})`, count: securityRisk.instancesWithDirectChannels, severity: 'critical', workspaces: securityRisk.directChannelWorkspaces });
		return items;
	}, [securityRisk]);

	const onlineAgents = agents.filter(a => a.status === 'online').length;
	const agentsNeedingUpdate = agents.filter(a => a.update_available && a.status === 'online');
	const runningInstances = instances.filter(i => i.running_status === 'running').length;

	// Count enabled channels across security instances
	const enabledChannels = useMemo(() => {
		const channelSet = new Set<string>();
		for (const inst of securityInstances) {
			const integrations = inst.config?.integrations;
			if (!integrations) continue;
			for (const [key, val] of Object.entries(integrations)) {
				if ((val as any)?.enabled) channelSet.add(key);
			}
		}
		return channelSet;
	}, [securityInstances]);

	// Count policies with threat detection enabled
	const policiesWithThreatDetection = securityInstances.filter(i => i.config?.enable_threat_detection).length;
	const policiesWithPam = securityInstances.filter(i => i.config?.enable_pam).length;

	// License info
	const license = currentTeam?.license;

	const getInstancesForAgent = (agentId: string) => instances.filter(i => i.agent_id === agentId);

	return (
		<Layout title="Overview" subtitle="Your fleet at a glance — devices, bots, and workspaces">
			{/* Connector Upgrade Banner */}
			{agentsNeedingUpdate.length > 0 && (
				<Link
					to={`/machines/${agentsNeedingUpdate[0].agent_id}`}
					className="block mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-4 hover:shadow-md transition-shadow"
				>
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<div className="p-2 bg-amber-100 dark:bg-amber-800/40 rounded-lg">
								<svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
								</svg>
							</div>
							<div>
								<p className="font-semibold text-amber-800 dark:text-amber-300">
									Connector Update Available
								</p>
								<p className="text-sm text-amber-700 dark:text-amber-400">
									{agentsNeedingUpdate.length === 1
										? `${agentsNeedingUpdate[0].hostname} — v${agentsNeedingUpdate[0].latest_version}`
										: `${agentsNeedingUpdate.length} machines need updating to v${agentsNeedingUpdate[0].latest_version}`
									}
								</p>
							</div>
						</div>
						<span className="px-4 py-2 bg-amber-600 text-white font-semibold rounded-lg text-sm whitespace-nowrap">
							Upgrade Now
						</span>
					</div>
				</Link>
			)}

			{/* Stats Row 1 */}
			<div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
				{/* Machines */}
				<Link to="/machines" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 hover:shadow-lg transition-shadow">
					<div className="flex items-center justify-between mb-3">
						<div className="p-2.5 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl shadow-md">
							<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
							</svg>
						</div>
						<span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
							onlineAgents > 0
								? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
								: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
						}`}>
							{onlineAgents} online
						</span>
					</div>
					<h3 className="text-2xl font-bold text-slate-900 dark:text-white">{agents.length}</h3>
					<p className="text-sm text-slate-500 dark:text-slate-400">Devices</p>
				</Link>

				{/* AI Agents */}
				<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
					<div className="flex items-center justify-between mb-3">
						<div className="p-2.5 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl shadow-md">
							<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
							</svg>
						</div>
						<span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
							runningInstances > 0
								? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
								: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
						}`}>
							{runningInstances} running
						</span>
					</div>
					<h3 className="text-2xl font-bold text-slate-900 dark:text-white">{instances.length}</h3>
					<p className="text-sm text-slate-500 dark:text-slate-400">Workspaces</p>
				</div>

				{/* Security Score */}
				<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 relative">
					<div className="flex items-center justify-between mb-3">
						<div className={`p-2.5 rounded-xl shadow-md ${
							securityRisk.overallRisk === 'good'
								? 'bg-gradient-to-br from-green-400 to-green-600'
								: securityRisk.overallRisk === 'warning'
								? 'bg-gradient-to-br from-yellow-400 to-yellow-600'
								: 'bg-gradient-to-br from-red-400 to-red-600'
						}`}>
							<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
							</svg>
						</div>
						<button
							onClick={() => issueCount > 0 && setShowIssues(!showIssues)}
							className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${
								issueCount === 0
									? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
									: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-900/50 cursor-pointer'
							}`}
						>
							{issueCount} issue{issueCount !== 1 ? 's' : ''}{issueCount > 0 ? (showIssues ? ' \u25B2' : ' \u25BC') : ''}
						</button>
					</div>
					<h3 className={`text-2xl font-bold ${
						securityRisk.overallRisk === 'good'
							? 'text-green-600 dark:text-green-400'
							: securityRisk.overallRisk === 'warning'
							? 'text-yellow-600 dark:text-yellow-400'
							: 'text-red-600 dark:text-red-400'
					}`}>
						{securityRisk.overallRisk === 'good' ? 'Good' : securityRisk.overallRisk === 'warning' ? 'Fair' : 'Poor'}
					</h3>
					<p className="text-sm text-slate-500 dark:text-slate-400">Safety Score</p>
					{showIssues && issueDetails.length > 0 && (
						<div className="absolute top-full left-0 right-0 mt-2 z-20 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg p-3 min-w-[280px]">
							<div className="space-y-2">
								{issueDetails.map((issue, i) => (
									<div key={i} className="flex items-start gap-2">
										<span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
											issue.severity === 'critical' ? 'bg-red-500' : 'bg-yellow-500'
										}`} />
										<div className="flex-1 min-w-0">
											{issue.link ? (
												<Link
													to={issue.link}
													className="text-xs text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
												>
													{issue.label}
												</Link>
											) : (
												<span className="text-xs text-slate-700 dark:text-slate-300">
													{issue.label}
												</span>
											)}
											<div className="flex flex-wrap gap-1 mt-0.5">
												{issue.workspaces.map((ws, j) => (
													<span key={j} className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400">
														{ws}
													</span>
												))}
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>

				{/* Channels */}
				<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
					<div className="flex items-center justify-between mb-3">
						<div className="p-2.5 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl shadow-md">
							<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.858 15.355-5.858 21.213 0" />
							</svg>
						</div>
					</div>
					<h3 className="text-2xl font-bold text-slate-900 dark:text-white">{enabledChannels.size}</h3>
					<p className="text-sm text-slate-500 dark:text-slate-400">Integrations Enabled</p>
					{enabledChannels.size > 0 && (
						<p className="text-xs text-slate-400 dark:text-slate-500 mt-1 truncate">
							{Array.from(enabledChannels).map(c => c.charAt(0).toUpperCase() + c.slice(1).replace('_', ' ')).join(', ')}
						</p>
					)}
				</div>
			</div>

			{/* Stats Row 2 */}
			<div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
				{/* Policies — Threat Detection */}
				<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
					<div className="flex items-center space-x-3 mb-2">
						<svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
						</svg>
						<span className="text-sm font-medium text-slate-700 dark:text-slate-300">Threat Protection</span>
					</div>
					<p className="text-lg font-bold text-slate-900 dark:text-white">
						{policiesWithThreatDetection}/{securityInstances.length}
					</p>
					<p className="text-xs text-slate-400 dark:text-slate-500">workspaces protected</p>
				</div>

				{/* Policies — PAM */}
				<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
					<div className="flex items-center space-x-3 mb-2">
						<svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
						</svg>
						<span className="text-sm font-medium text-slate-700 dark:text-slate-300">Access Control</span>
					</div>
					<p className="text-lg font-bold text-slate-900 dark:text-white">
						{policiesWithPam}/{securityInstances.length}
					</p>
					<p className="text-xs text-slate-400 dark:text-slate-500">access control enabled</p>
				</div>

				{/* Team Members */}
				<Link to="/team" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 hover:shadow-lg transition-shadow">
					<div className="flex items-center space-x-3 mb-2">
						<svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
						</svg>
						<span className="text-sm font-medium text-slate-700 dark:text-slate-300">Team</span>
					</div>
					<p className="text-lg font-bold text-slate-900 dark:text-white">
						{teamMemberCount}
					</p>
					<p className="text-xs text-slate-400 dark:text-slate-500">
						member{teamMemberCount !== 1 ? 's' : ''}
						{license && ` / ${license.seat_limit} seats`}
					</p>
				</Link>

				{/* License */}
				<Link to="/billing" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 hover:shadow-lg transition-shadow">
					<div className="flex items-center space-x-3 mb-2">
						<svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
						</svg>
						<span className="text-sm font-medium text-slate-700 dark:text-slate-300">License</span>
					</div>
					<p className="text-lg font-bold text-slate-900 dark:text-white capitalize">
						{license?.type === 'active' ? 'Pro' : license?.type || 'Trial'}
					</p>
					<p className="text-xs text-slate-400 dark:text-slate-500">
						{license?.type === 'active'
							? `${license.agent_limit} workspace${license.agent_limit !== 1 ? 's' : ''} · ${license.seat_limit} seat${license.seat_limit !== 1 ? 's' : ''}`
							: license?.days_remaining != null
							? `${license.days_remaining} day${license.days_remaining !== 1 ? 's' : ''} remaining · ${license.agent_limit} workspace${license.agent_limit !== 1 ? 's' : ''} · ${license.seat_limit} seat${license.seat_limit !== 1 ? 's' : ''}`
							: 'Free tier'}
					</p>
				</Link>
			</div>

			{/* Machine Cards with nested AI Agents */}
			{agents.length > 0 && (
				<div className="mb-8">
					<div className="flex items-center justify-between mb-4">
						<h2 className="font-heading text-xl font-bold text-slate-900 dark:text-white">Devices</h2>
						<Link
							to="/machines"
							className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
						>
							View All
						</Link>
					</div>
					<div className="space-y-4">
						{agents.map((agent) => {
							const agentInstances = getInstancesForAgent(agent.agent_id);
							return (
								<Link
									key={agent.agent_id}
									to={`/machines/${agent.agent_id}`}
									className="block bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 hover:shadow-lg transition-shadow"
								>
									<div className="flex items-center justify-between mb-3">
										<div className="flex items-center space-x-3">
											<div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${agent.status === 'online' ? 'bg-green-500' : 'bg-slate-400'}`} />
											<div>
												<h3 className="font-semibold text-slate-900 dark:text-white">{agent.hostname}</h3>
												<p className="text-xs text-slate-500 dark:text-slate-400">
													{agent.platform} &middot; v{agent.version}
													{agent.update_available && (
														<span className="ml-1 text-amber-600 dark:text-amber-400 font-semibold">&middot; v{agent.latest_version} available</span>
													)}
												</p>
											</div>
											{agent.user_id !== user?.uid && (
												<span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
													Shared
												</span>
											)}
										</div>
										<span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
											agent.status === 'online'
												? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
												: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
										}`}>
											{agent.status}
										</span>
									</div>

									{/* AI Workspaces */}
									{agentInstances.length > 0 ? (
										<div className="ml-5 pl-3 border-l-2 border-slate-200 dark:border-slate-700 space-y-2">
											{agentInstances.map((inst) => (
												<div key={inst.instance_id} className="flex items-center justify-between py-1.5">
													<div className="flex items-center space-x-2">
														<div className={`w-1.5 h-1.5 rounded-full ${
															inst.running_status === 'running' ? 'bg-green-500' : inst.running_status === 'error' ? 'bg-red-500' : 'bg-slate-400'
														}`} />
														<span className="text-sm font-medium text-slate-700 dark:text-slate-300">
															{inst.instance_name || 'unnamed'}
														</span>
														<span className="text-xs text-slate-400 dark:text-slate-500">
															{inst.bot_type === 'openclaw' ? 'OpenClaw' : inst.bot_type || 'Bot'}
														</span>
													</div>
													<span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
														inst.running_status === 'running'
															? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
															: inst.running_status === 'error'
															? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
															: 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
													}`}>
														{inst.running_status}
													</span>
												</div>
											))}
										</div>
									) : (
										<p className="ml-5 pl-3 border-l-2 border-slate-200 dark:border-slate-700 text-xs text-slate-400 dark:text-slate-500 py-1">
											No bots found on this device
										</p>
									)}
								</Link>
							);
						})}
					</div>
				</div>
			)}

			{/* Getting Started */}
			<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
				<div className="flex items-start justify-between mb-4">
					<div>
						<h2 className="font-heading text-xl font-bold text-slate-900 dark:text-white mb-1">
							{agents.length === 0 ? 'Get Started with Clawdgate' : 'Add Another Device'}
						</h2>
						<p className="text-sm text-slate-600 dark:text-slate-400">
							{agents.length === 0
								? 'Secure and manage your bots with threat protection, policy enforcement, and remote control'
								: 'Install the Clawdgate connector on another device to extend your security coverage'}
						</p>
					</div>
					<button
						onClick={() => setShowInstallInstructions(!showInstallInstructions)}
						className="flex-shrink-0 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer text-sm"
					>
						{showInstallInstructions ? 'Hide' : 'Show'} Instructions
					</button>
				</div>

				{showInstallInstructions && (
					<div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
						<PlatformInstallGuide
							registrationToken={registrationToken}
							tokenLoading={tokenLoading}
							tokenError={tokenError}
						/>
					</div>
				)}
			</div>
		</Layout>
	);
}
